if (!customElements.get('dg-product-landing')) {
  customElements.define('dg-product-landing', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.data = JSON.parse(this.querySelector('[data-dg-variant-data]').textContent);
      this.variant = this.data.variants.find(item => item.id === this.data.selectedId);
      this.refreshArrivalDates = () => this.updateArrivalWindows();
      this.refreshArrivalDates();
      window.addEventListener('pageshow', this.refreshArrivalDates);
      this.form = this.querySelector('product-form form');
      this.quantity = this.form.querySelector('[name="quantity"]');
      this.formatter = new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.pair = this.querySelector('[data-dg-pair]');
      this.bundle = this.querySelector('[data-dg-bundle]');
      if (this.querySelector('[data-dg-input-app]')) {
        this.schedulePersonalizationSync();
        this.addEventListener('focusin', event => {
          if (event.target.closest('[data-dg-input-app]')) this.syncPersonalizationFields();
        });
      }
      this.form.addEventListener('submit', event => {
        this.syncPersonalizationFields();
        const fields = this.querySelectorAll('[data-dg-input-app] input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), [data-dg-input-app] textarea');
        const missingPersonalization = this.data.personalizationRequired && !Array.from(fields).some(field => !field.disabled && field.value.trim());
        if (missingPersonalization || !this.form.reportValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (missingPersonalization) this.showPurchaseError(this.data.labels.personalizationLoading);
          return;
        }
        if (this.bundle && Number(this.quantity.value) > 1) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addGiftSet();
        }
      }, true);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-dg-option] input, [data-dg-option] select')) {
          this.selectVariant();
          if (this.querySelector('[data-dg-input-app]')) this.schedulePersonalizationSync();
        }
        if (event.target.matches('[name="quantity"]')) { this.normalizeQuantity(); this.updateGiftSet(); }
        if (event.target.matches('[data-dg-pair-variant]')) this.updatePair();
        if (event.target.matches('[name="_dg_bundle_tier"]')) {
          this.quantity.value = event.target.value;
          this.normalizeQuantity();
          this.updateGiftSet();
        }
      });
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-dg-thumbnail]');
        if (thumb) this.showMedia(thumb.dataset.dgThumbnail);
        const quantityButton = event.target.closest('[data-dg-quantity]');
        if (quantityButton) {
          const step = Number(this.quantity.step) || 1;
          this.quantity.value = (Number(this.quantity.value) || 1) + Number(quantityButton.dataset.dgQuantity) * step;
          this.normalizeQuantity();
        }
        const zoom = event.target.closest('[data-dg-zoom]');
        if (zoom) this.openZoom(zoom);
        if (event.target.closest('[data-dg-close-zoom]')) this.querySelector('[data-dg-lightbox]').close();
        if (event.target.closest('[data-dg-add-pair]')) this.addPair();
        const radioLabel = event.target.closest('.dg-pdp-option-values label');
        if (radioLabel) this.querySelector('.product-form__error-message-wrapper').hidden = true;
      });
      const dialog = this.querySelector('[data-dg-lightbox]');
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      this.submitButton = this.querySelector('[data-dg-submit]');
      this.submitObserver = new MutationObserver(() => this.syncSticky());
      this.submitObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      this.updateStickyPosition = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          this.querySelector('[data-dg-sticky]').hidden = this.querySelector('[data-dg-purchase]').getBoundingClientRect().bottom >= 0;
        });
      };
      window.addEventListener('scroll', this.updateStickyPosition, { passive: true });
      window.addEventListener('resize', this.updateStickyPosition, { passive: true });
      this.updateStickyPosition();
      this.querySelectorAll('[data-dg-add-pair]').forEach(button => { button.disabled = !this.variant?.available; });
      this.updatePair();
      if (this.variant) this.updateOptionAvailability(this.variant.options);
      this.dataset.enhanced = 'true';
      if (this.bundle && this.data.bundle?.tiers?.length) {
        this.bundle.hidden = false;
        this.quantity.closest('.dg-pdp-quantity').hidden = true;
        this.updateGiftSet();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.updateStickyPosition);
      window.removeEventListener('resize', this.updateStickyPosition);
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      this.submitObserver?.disconnect();
      window.clearTimeout(this.personalizationTimer);
      window.removeEventListener('pageshow', this.refreshArrivalDates);
    }

    schedulePersonalizationSync() {
      window.clearTimeout(this.personalizationTimer);
      let attempts = 0;
      const refresh = () => {
        if (!this.isConnected) return;
        this.syncPersonalizationFields();
        attempts += 1;
        if (attempts < 20) this.personalizationTimer = window.setTimeout(refresh, 250);
      };
      refresh();
    }

    syncPersonalizationFields() {
      const formId = this.form?.getAttribute('id');
      if (!formId) return;
      const watchLabels = {
        'properties[watch-field-1]': 'Engraving line 1',
        'properties[watch-field-2]': 'Engraving line 2 (optional)'
      };
      this.querySelectorAll('[data-dg-input-app] input, [data-dg-input-app] select, [data-dg-input-app] textarea').forEach(field => {
        if (!field.name?.startsWith('properties[')) return;
        // The native app can write a CSS selector into the HTML form attribute.
        // Associate its controls with this product's actual form ID instead.
        if (field.getAttribute('form') !== formId) field.setAttribute('form', formId);
        if (field.matches('input[type="hidden"], input[type="radio"], input[type="checkbox"]')) return;
        const label = field.closest('.so-field-wrapper')?.querySelector('.so-form__label');
        if (!label) return;
        if (field.id && !label.htmlFor) label.htmlFor = field.id;
        const displayLabel = watchLabels[field.name];
        if (displayLabel && label.textContent.trim() !== displayLabel) label.textContent = displayLabel;
      });
    }

    deliveryHolidays(year) {
      this.deliveryCalendars ||= new Map();
      if (this.deliveryCalendars.has(year)) return this.deliveryCalendars.get(year);
      const keys = new Set();
      const key = date => date.toISOString().slice(0, 10);
      const observed = (y, month, day) => {
        const date = new Date(Date.UTC(y, month, day));
        if (date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() - 1);
        if (date.getUTCDay() === 0) date.setUTCDate(date.getUTCDate() + 1);
        keys.add(key(date));
      };
      const monday = (month, occurrence) => {
        const date = new Date(Date.UTC(year, month, 1));
        date.setUTCDate(1 + (8 - date.getUTCDay()) % 7 + (occurrence - 1) * 7);
        keys.add(key(date));
      };
      observed(year, 0, 1);
      observed(year + 1, 0, 1);
      observed(year, 5, 19);
      observed(year, 6, 4);
      observed(year, 10, 11);
      observed(year, 11, 25);
      monday(0, 3);
      monday(1, 3);
      monday(8, 1);
      monday(9, 2);
      const memorial = new Date(Date.UTC(year, 5, 0));
      memorial.setUTCDate(memorial.getUTCDate() - (memorial.getUTCDay() + 6) % 7);
      keys.add(key(memorial));
      const thanksgiving = new Date(Date.UTC(year, 10, 1));
      thanksgiving.setUTCDate(1 + (11 - thanksgiving.getUTCDay()) % 7 + 21);
      keys.add(key(thanksgiving));
      this.deliveryCalendars.set(year, keys);
      return keys;
    }

    deliveryBusinessDate(start, count) {
      const date = new Date(start);
      const working = day => day.getUTCDay() !== 0 && day.getUTCDay() !== 6 && !this.deliveryHolidays(day.getUTCFullYear()).has(day.toISOString().slice(0, 10));
      if (count === 0) {
        while (!working(date)) date.setUTCDate(date.getUTCDate() + 1);
        return date;
      }
      let remaining = count;
      while (remaining > 0) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (working(date)) remaining -= 1;
      }
      return date;
    }

    updateArrivalWindows() {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const values = Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
      const today = new Date(Date.UTC(values.year, values.month - 1, values.day));
      this.querySelectorAll('[data-dg-arrival]').forEach(box => {
        const output = box.querySelector('[data-dg-arrival-range]');
        if (!output) return;
        if (!this.variant?.available) {
          output.textContent = box.dataset.arrivalUnavailable;
          delete output.dataset.earliest;
          delete output.dataset.latest;
          return;
        }
        const preparation = /same.day/i.test(box.dataset.preparation) ? [0, 1] : (box.dataset.preparation.match(/\d+(?:\.\d+)?/g) || []).map(Number);
        const transit = (box.dataset.transit.match(/\d+(?:\.\d+)?/g) || []).map(Number);
        if (!output || !preparation.length || !transit.length) return;
        if ([...preparation, ...transit].some(value => !Number.isFinite(value) || value < 0 || value > 90)) return;
        const average = transit.length === 1;
        const earliestDays = Math.floor(Math.min(...preparation) + (average ? Math.max(1, transit[0] - 2) : Math.min(...transit)));
        const latestDays = Math.ceil(Math.max(...preparation) + (average ? transit[0] + 3 : Math.max(...transit)));
        const earliest = this.deliveryBusinessDate(today, earliestDays);
        const latest = this.deliveryBusinessDate(today, latestDays);
        const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC', ...(latest.getUTCFullYear() !== today.getUTCFullYear() ? { year: 'numeric' } : {}) });
        output.textContent = formatter.formatRange(earliest, latest);
        output.dataset.earliest = earliest.toISOString().slice(0, 10);
        output.dataset.latest = latest.toISOString().slice(0, 10);
      });
    }

    money(value) { return this.formatter.format(value / 100); }

    valueLabel(value) { return this.data.valueLabels?.[value] || value || ''; }

    variantTitle(variant) { return variant ? variant.options.map(value => this.valueLabel(value)).join(' / ') : ''; }

    normalizeQuantity() {
      const min = Number(this.quantity.min) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const step = Number(this.quantity.step) || 1;
      const value = Number(this.quantity.value);
      this.quantity.value = Math.min(max, Math.max(min, min + Math.round(((Number.isFinite(value) ? value : min) - min) / step) * step));
    }

    showMedia(id) {
      const target = this.querySelector(`[data-dg-media="${id}"]`);
      if (!target) return;
      const image = target.querySelector('img');
      if (image) image.loading = 'eager';
      this.querySelectorAll('[data-dg-media]').forEach(slide => {
        slide.hidden = slide !== target;
        if (slide !== target) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-dg-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.dgThumbnail === String(id))));
    }

    selectVariant() {
      const choices = [];
      this.querySelectorAll('[data-dg-option]').forEach(group => { choices[Number(group.dataset.dgOption)] = group.querySelector('input:checked, select')?.value; });
      this.querySelectorAll('[data-dg-fixed-option]').forEach(input => { choices[Number(input.dataset.dgFixedOption)] = input.value; });
      this.variant = this.data.variants.find(item => item.options.every((value, index) => value === choices[index]));
      this.querySelectorAll('[data-dg-option]').forEach(group => { group.querySelector('[data-dg-choice]').textContent = this.valueLabel(choices[Number(group.dataset.dgOption)]); });
      this.updateProduct();
      this.updateOptionAvailability(choices);
    }

    updateOptionAvailability(choices) {
      this.querySelectorAll('[data-dg-option]').forEach(group => {
        const index = Number(group.dataset.dgOption);
        group.querySelectorAll('input[type="radio"]').forEach(input => {
          const matching = this.data.variants.filter(item => item.options[index] === input.value && item.options.every((value, i) => i === index || value === choices[i]));
          input.nextElementSibling.classList.toggle('dg-pdp-option-unavailable', !matching.some(item => item.available));
        });
        group.querySelectorAll('select option').forEach(option => {
          const matching = this.data.variants.filter(item => item.options[index] === option.value && item.options.every((value, i) => i === index || value === choices[i]));
          // Existing sold-out variants remain selectable for a restock alert.
          // Combinations that are not actual SKUs cannot be selected.
          option.disabled = matching.length === 0;
        });
      });
    }

    priceDisplay() {
      const variant = this.variant;
      if (!variant) return { price: null, compare: 0, basis: '' };
      let price = variant.price;
      const packSaving = variant.packCompare > price;
      let compare = packSaving ? variant.packCompare : variant.compare || 0;
      let basis = packSaving ? this.data.labels.packBasis.replace('[count]', String(variant.packUnits)) : '';
      const quantity = Number(this.quantity?.value) || 1;
      const tier = this.bundle && this.data.bundle?.tiers?.find(item => item.quantity === quantity);
      if (tier && quantity > 1 && tier.percent > 0) {
        price = (variant.price - Math.floor(variant.price * tier.percent / 100)) * quantity;
        compare = variant.price * quantity;
        basis = this.data.labels.packBasis.replace('[count]', String(quantity));
      }
      return { price, compare, basis };
    }

    updatePrice() {
      const display = this.priceDisplay();
      const hasSaving = display.price !== null && display.compare > display.price;
      this.querySelector('[data-dg-price]').textContent = display.price === null ? '—' : this.money(display.price);
      this.querySelector('[data-dg-sticky-price]').textContent = display.price === null ? '—' : this.money(display.price);
      const compare = this.querySelector('[data-dg-compare]');
      const saving = this.querySelector('[data-dg-saving]');
      compare.hidden = saving.hidden = !hasSaving;
      compare.textContent = hasSaving ? this.money(display.compare) : '';
      saving.textContent = hasSaving ? `${this.data.labels.save} ${this.money(display.compare - display.price)}` : '';
      const basis = this.querySelector('[data-dg-price-basis]');
      if (basis) {
        basis.hidden = !hasSaving || !display.basis;
        basis.textContent = hasSaving ? display.basis : '';
      }
    }

    updateProduct() {
      const variant = this.variant;
      const enabled = Boolean(variant?.available);
      const selectedTitle = this.querySelector('[data-dg-selected-title]');
      if (selectedTitle) selectedTitle.textContent = this.variantTitle(variant) || this.data.labels.unavailable;
      const restockMount = this.querySelector('[data-dg-restock-mount]');
      if (restockMount) {
        restockMount.hidden = !variant || enabled;
        restockMount.dataset.variantId = variant?.id || '';
      }
      this.querySelectorAll('[data-dg-delivery-restock]').forEach(note => { note.hidden = enabled; });
      const accelerated = this.querySelector('.dg-pdp-accelerated');
      if (accelerated) accelerated.hidden = !enabled;
      this.form.querySelector('[name="id"]').value = variant?.id || '';
      this.form.querySelector('[name="id"]').disabled = !variant;
      this.form.querySelector('[name="id"]').dispatchEvent(new Event('change', { bubbles: true }));
      this.submitButton.disabled = !enabled;
      this.submitButton.removeAttribute('aria-disabled');
      this.querySelectorAll('[data-dg-button-label]').forEach(label => { label.textContent = !variant ? this.data.labels.unavailable : enabled ? this.data.labels.add : this.data.labels.soldOut; });
      this.updatePrice();
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        this.showMedia(variant.media || this.data.fallbackMedia);
        this.quantity.min = variant.rule?.min || 1;
        this.quantity.step = variant.rule?.increment || 1;
        if (variant.rule?.max) this.quantity.max = variant.rule.max;
        else this.quantity.removeAttribute('max');
        this.normalizeQuantity();
        const unit = this.querySelector('[data-dg-unit]');
        unit.hidden = !variant.unit;
        if (variant.unit) unit.textContent = `${this.money(variant.unitPrice)} / ${variant.unit.reference_value === 1 ? '' : variant.unit.reference_value}${variant.unit.reference_unit}`;
      }
      this.querySelectorAll('[data-dg-add-pair]').forEach(button => { button.disabled = !enabled; });
      this.updatePair();
      this.updateGiftSet();
      this.syncSticky();
      this.refreshArrivalDates();
      this.dispatchEvent(new CustomEvent('dg:variant-change', { bubbles: true, detail: { variant } }));
    }

    syncSticky() {
      const button = this.querySelector('[data-dg-sticky-submit]');
      button.disabled = this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true';
      button.setAttribute('aria-busy', String(this.submitButton.classList.contains('loading')));
    }

    updatePair() {
      if (!this.pair) return;
      const selected = this.pair.querySelector('[data-dg-pair-variant]').selectedOptions[0];
      const price = Number(selected?.dataset.price) || 0;
      this.pair.querySelector('[data-dg-pair-main-name]').textContent = this.variantTitle(this.variant);
      this.pair.querySelector('[data-dg-pair-main-price]').textContent = this.variant ? this.money(this.variant.price) : '—';
      this.pair.querySelector('[data-dg-pair-addon-price]').textContent = this.money(price);
      this.pair.querySelector('[data-dg-pair-total]').textContent = this.variant ? this.money(this.variant.price + price) : '—';
    }

    async addPair() {
      if (!this.variant?.available || !this.pair) return;
      const button = this.pair.querySelector('[data-dg-add-pair]');
      if (button.disabled) return;
      const error = this.pair.querySelector('[data-dg-pair-error]');
      const addon = this.pair.querySelector('[data-dg-pair-variant]').selectedOptions[0];
      if (!addon || addon.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = { items: [{ id: this.variant.id, quantity: 1 }, { id: Number(addon.value), quantity: 1 }] };
      if (cart) {
        payload.sections = cart.getSectionsToRender().map(section => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }
      try {
        const response = await fetch(`${this.dataset.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(payload) });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || this.data.labels.error);
        if (cart) {
          cart.classList.remove('is-empty');
          cart.renderContents({ ...data, key: data.items?.[0]?.key });
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dg-product-pair' });
        } else window.location.assign(`${this.dataset.root}cart`);
      } catch (reason) {
        error.textContent = reason.message || this.data.labels.error;
        error.hidden = false;
      } finally {
        button.disabled = !this.variant?.available;
        button.removeAttribute('aria-busy');
      }
    }

    showPurchaseError(message) {
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      wrapper.querySelector('.product-form__error-message').textContent = message;
      wrapper.hidden = false;
    }

    updateGiftSet() {
      this.updatePrice();
      if (!this.bundle || !this.variant) return;
      const selectedQuantity = Number(this.quantity.value) || 1;
      this.data.bundle.tiers.forEach(tier => {
        const radio = this.bundle.querySelector(`[value="${tier.quantity}"]`);
        radio.disabled = !this.variant.available || (this.variant.rule?.max && tier.quantity > this.variant.rule.max) || tier.quantity < (this.variant.rule?.min || 1);
        radio.checked = tier.quantity === selectedQuantity;
        const original = this.bundle.querySelector(`[data-dg-set-original="${tier.quantity}"]`);
        if (original) {
          original.textContent = this.money(this.variant.price * tier.quantity);
          original.hidden = tier.percent === 0;
        }
        const discountedUnit = this.variant.price - Math.floor(this.variant.price * tier.percent / 100);
        const total = discountedUnit * tier.quantity;
        this.bundle.querySelector(`[data-dg-set-price="${tier.quantity}"]`).textContent = this.money(total);
      });
      this.querySelectorAll('[data-dg-delivery-restock]').forEach(note => { note.hidden = this.variant.available; });
      const accelerated = this.querySelector('.dg-pdp-accelerated');
      if (accelerated) accelerated.hidden = selectedQuantity > 1 || !this.variant.available;
      const paymentTerms = this.querySelector('.dg-pdp-payment-terms');
      if (paymentTerms) paymentTerms.hidden = selectedQuantity > 1;
      this.querySelectorAll('[data-dg-button-label]').forEach(label => {
        label.textContent = this.bundleAdded ? this.data.labels.viewCart : !this.variant.available ? this.data.labels.soldOut : selectedQuantity > 1 ? this.data.labels.addSet : this.data.labels.add;
      });
    }

    async addGiftSet() {
      if (this.bundleAdded) { window.location.assign(`${this.dataset.root}cart`); return; }
      const tier = this.data.bundle?.tiers?.find(item => item.quantity === Number(this.quantity.value));
      if (this.data.bundle.mode === 'automatic') { await this.addAutomaticSet(tier); return; }
      if (!tier?.code || !this.variant?.available || this.bundleAdding) return;
      this.bundleAdding = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.querySelector('.product-form__error-message-wrapper').hidden = true;
      let added = false;
      try {
        const currentResponse = await fetch(`${this.dataset.root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!currentResponse.ok) throw new Error(this.data.labels.error);
        const current = await currentResponse.json();
        const codes = (current.discount_codes || []).filter(item => item.applicable && !item.code.toUpperCase().startsWith('DUDUSET')).map(item => item.code);
        const formData = new FormData(this.form);
        formData.set('id', String(this.variant.id));
        formData.set('quantity', String(tier.quantity));
        formData.delete('_dg_bundle_tier');
        const addResponse = await fetch(`${this.dataset.root}cart/add.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: formData });
        const addResult = await addResponse.json();
        if (!addResponse.ok || addResult.status) throw new Error(addResult.description || this.data.labels.error);
        added = this.bundleAdded = true;
        codes.push(tier.code);
        const offerResponse = await fetch(`${this.dataset.root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: [...new Set(codes)].join(',') }) });
        const offerResult = await offerResponse.json();
        if (!offerResponse.ok || offerResult.status || !(offerResult.discount_codes || []).some(item => item.applicable)) throw new Error(this.data.labels.addedWithoutOffer);
        window.location.assign(`${this.dataset.root}cart`);
      } catch (error) {
        this.showPurchaseError(added ? this.data.labels.addedWithoutOffer : error.message || this.data.labels.error);
      } finally {
        this.bundleAdding = false;
        this.submitButton.disabled = !this.variant?.available;
        this.submitButton.removeAttribute('aria-busy');
        this.updateGiftSet();
      }
    }

    async addAutomaticSet(tier) {
      if (!tier || !this.variant?.available || this.bundleAdding || !window.DgGiftOffers) return;
      this.bundleAdding = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.querySelector('.product-form__error-message-wrapper').hidden = true;
      try {
        const properties = {};
        new FormData(this.form).forEach((value, key) => { const match = key.match(/^properties\[(.+)\]$/); if (match && typeof value === 'string') properties[match[1]] = value; });
        await window.DgGiftOffers.add({ items: [{ id: this.variant.id, quantity: tier.quantity, properties }], root: this.dataset.root, opener: this.submitButton, labels: { ...this.data.labels, partial: this.data.labels.addedWithoutOffer } });
      } catch (error) {
        if (error.cartAdded) this.bundleAdded = true;
        this.showPurchaseError(error.message || this.data.labels.error);
      } finally {
        this.bundleAdding = false;
        this.submitButton.disabled = !this.variant?.available;
        this.submitButton.removeAttribute('aria-busy');
        this.updateGiftSet();
      }
    }

    openZoom(trigger) {
      const dialog = this.querySelector('[data-dg-lightbox]');
      dialog.querySelector('[data-dg-zoom-image]').src = trigger.dataset.dgZoom;
      dialog.showModal();
    }
  });
}



